import { useFormContext } from 'react-hook-form';
import { RadioField, TextField } from '@views/register/history/history-fields';
import type { TLocalize } from '@hooks/use-localize';
import type { IHistoryFamilyFormValues, IHistoryFamilyModel } from '@/types/history/history-family';

interface IProps {
    model: IHistoryFamilyModel;
    t: TLocalize;
}

type TName = keyof IHistoryFamilyFormValues;

/** A "does anyone have a history of X" question plus its Yes-only follow-up. */
const ConditionQuestion = ({
    name,
    followUpName,
    label,
    followUpLabel,
    options,
    yes,
}: {
    name: TName;
    followUpName: TName;
    label: string;
    followUpLabel: string;
    options: IHistoryFamilyModel['yesNoUnknownOptions'];
    yes: string;
}) => {
    const { watch } = useFormContext<IHistoryFamilyFormValues>();

    return (
        <>
            <RadioField name={name} label={label} options={options} />

            {watch(name) === yes && (
                <div className="sub-question">
                    <TextField name={followUpName} label={followUpLabel} maxLength={128} />
                </div>
            )}
        </>
    );
};

const ConditionsSection = ({ model, t }: IProps) => {
    const yn = model.yesNoUnknownOptions;
    const yes = String(model.yesValue);
    const which = t('DashLabel_WhichFamilyMembers', 'Which family members');

    const question = (name: TName, followUpName: TName, label: string, followUpLabel = which) => (
        <ConditionQuestion
            name={name}
            followUpName={followUpName}
            label={label}
            followUpLabel={followUpLabel}
            options={yn}
            yes={yes}
        />
    );

    return (
        <>
            <div className="section-heading" style={{ paddingTop: 0 }}>
                {t('DashLabel_FamilyConditions', 'Conditions')}
            </div>

            {question(
                'familyHistoryKidneyDisease',
                'familyMembersKidneyDisease',
                t('DashLabel_DoesAnyoneHaveAHistoryOfKidneyDisease', 'Does anyone have a history of Kidney Disease'),
            )}

            {question(
                'familyHistoryLiverDisease',
                'familyMembersLiverDisease',
                t('DashLabel_DoesAnyoneHaveAHistoryOfLiverDisease', 'Does anyone have a history of Liver Disease'),
            )}

            {model.isLiverRegistration && (
                <>
                    {question(
                        'familyHistoryBowelIssues',
                        'familyMembersBowelIssues',
                        t('DashLabel_DoesAnyoneHaveAHistoryOfGastrointestinalBowelIssues', 'Does anyone have a history of Gastrointestinal (bowel) issues'),
                    )}

                    {question(
                        'familyHistoryLupusLiver',
                        'familyMembersLupusLiver',
                        t('DashLabel_DoesAnyoneHaveAHistoryOfLupus', 'Does anyone have a history of Lupus'),
                    )}
                </>
            )}

            {question(
                'familyHistoryHeartDisease',
                'familyMembersHeartDisease',
                t('DashLabel_DoesAnyoneHaveAHistoryOfHeartDisease', 'Does anyone have a history of Heart Disease'),
            )}

            {question(
                'familyHistoryCad',
                'familyMembersCad',
                t('DashLabel_DoesAnyoneHaveAHistoryOfCoronaryArteryDisease', 'Does anyone have a history of Coronary Artery Disease'),
            )}

            {question(
                'familyHistoryBloodPressure',
                'familyMembersBloodPressure',
                t('DashLabel_DoesAnyoneHaveAHistoryOfHighBloodPressureHypertension', 'Does anyone have a history of High Blood Pressure (hypertension)'),
            )}

            {question(
                'familyHistoryBloodDisorder',
                'familyMembersBloodDisorder',
                t('DashLabel_DoesAnyoneHaveAHistoryOfABloodDisorder', 'Does anyone have a history of a blood disorder'),
            )}

            {question(
                'familyHistoryCancer',
                'familyMembersCancer',
                t('DashLabel_DoesAnyoneHaveAHistoryOfCancer', 'Does anyone have a history of Cancer'),
            )}

            {question(
                'familyHistoryKidneyCancer',
                'familyMembersKidneyCancer',
                t('DashLabel_DoesAnyoneHaveAHistoryOfKidneyCancer', 'Does anyone have a history of Kidney Cancer'),
            )}

            {question(
                'familyHistoryDiabetes',
                'familyMembersDiabetes',
                t('DashLabel_DoesAnyoneHaveAHistoryOfDiabetes', 'Does anyone have a history of Diabetes'),
            )}

            {model.isLiverRegistration &&
                question(
                    'familyMemberSuddenDeathUnder50',
                    'familyMemberSuddenDeathUnder50Description',
                    t('DashLabel_HasAnyoneInYourFamilySuddenlyDiedBeforeTheAgeOf50', 'Has anyone in your family suddenly died before the age of 50'),
                    t('DashLabel_PleaseDescribe', 'Please describe'),
                )}
        </>
    );
};

export default ConditionsSection;
